import Decimal from 'decimal.js'
import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { config } from '../../appConfig'
import ChainFusionClient from '../../clients/ChainFusionClient'
import HyperLiquidClient from '../../clients/HyperLiquidClient'
import { HyperLiquidOrder, PerpDirection, PerpPosition } from '../../interfaces/hyperliquid'
import { logger } from '../../logger'
import { formatMoney, formatNumber } from '../../utils/formatNumber'
import { prepareHyperliquidSignature } from '../../utils/hyperliquidSigning'
import useBalances from '../useBalances'
import useConfig from '../useConfig'
import useWallet from '../useWallet'
import useApproveBuilderFee from './useApproveBuilderFee'
import { usePerpAssetMeta } from './usePerpAssetMeta'

const MAX_DECIMALS = 6
const MIN_ORDER_SIZE_USD = 10.25

export type PerpEditType = 'increase' | 'reduce'

interface Props {
  hyperliquidTokenId: number
  type?: PerpDirection
  currentOrder?: PerpPosition
  editType?: PerpEditType
}

const useCreateOrder = ({ hyperliquidTokenId, type, currentOrder, editType }: Props) => {
  const [amount, setAmount] = useState<string>()
  const [leverage, setLeverage] = useState(1)
  const [loading, setLoading] = useState(false)
  const [executionError, setExecutionError] = useState<string>()

  const { perpsAccountBalance, refreshPerpsAccountBalance } = useBalances()
  const { perpsTokens, tokens } = useConfig()
  const perpToken = perpsTokens.find((t) => t.id === hyperliquidTokenId)
  const token = tokens.find((t) => t.additionalData.hyperliquidId === hyperliquidTokenId.toString())

  const { meta } = usePerpAssetMeta(hyperliquidTokenId)

  const hyperliquidClient = new HyperLiquidClient()

  const amountParsed = Decimal(amount || '0')
  const totalAmount = amountParsed.mul(leverage)
  const size = totalAmount.div(meta?.price || 1)
  const { wallet } = useWallet()

  const { t } = useTranslation()
  const { approveBuilderFee } = useApproveBuilderFee()

  useEffect(() => {
    if (currentOrder) {
      setLeverage(currentOrder.position.leverage.value)

      return
    }

    setLeverage(perpToken?.maxLeverage ? perpToken.maxLeverage : 1)
  }, [perpToken, currentOrder])

  const currentOrderSize = parseFloat(currentOrder?.position?.szi || '')

  const updateLeverage = async (
    asset: number,
    leverage: number,
    chainFusion: ChainFusionClient
  ) => {
    const nonce = Date.now()
    const updateLeverageAction = hyperliquidClient.prepareLeverageUpdate({
      asset,
      leverage,
    })

    const leverageDigest = prepareHyperliquidSignature(updateLeverageAction, nonce)
    const leverageSignature = await chainFusion.signEthHash(leverageDigest)

    if (!leverageSignature) throw 'Unable to sign leverage update'

    await hyperliquidClient.executeExchangeAction({
      action: updateLeverageAction,
      nonce,
      signature: leverageSignature,
    })
  }

  const publishOrder = async (order: HyperLiquidOrder, skipUpdateLeverage: boolean = false) => {
    await approveBuilderFee()
    const chainFusion = new ChainFusionClient(config.getAgent({ principalId: wallet.principalId }))

    if (!skipUpdateLeverage) await updateLeverage(hyperliquidTokenId, leverage, chainFusion)

    const nonce = Date.now()
    const digest = prepareHyperliquidSignature(order, nonce)
    const orderSignature = await chainFusion.signEthHash(digest)

    if (!orderSignature) return Promise.reject('Unable to sign')

    return hyperliquidClient.executeExchangeAction({
      action: order,
      nonce,
      signature: orderSignature,
    })
  }

  const snapPriceToTick = (price: number, multiplier: number, decimals: number) => {
    const decimalPlaces = MAX_DECIMALS - decimals

    return Decimal(price).mul(multiplier).toSignificantDigits(5).toFixed(decimalPlaces)
  }

  const totalSize = useMemo(() => {
    const currentOrderSize = Decimal(currentOrder?.position.szi || '0')
    const currentSizeChange = totalAmount.div(meta?.price || 1)

    if (currentOrder && editType === 'reduce') return currentOrderSize.sub(currentSizeChange)
    if (currentOrder && editType === 'increase') return currentOrderSize.add(currentSizeChange)

    return currentOrderSize
  }, [totalAmount, currentOrder, editType])

  const placeOrder = async () => {
    if (currentOrder) throw Error('Position already exists - use update')
    if (!meta) throw Error('No price')

    if (!token || !type) return

    const order = hyperliquidClient.prepareBuyOrder({
      assetId: hyperliquidTokenId,
      price: snapPriceToTick(
        meta.price,
        orderType === 'short' ? 0.9 : 1.1,
        perpToken?.szDecimals || 0
      ),
      size: size.toFixed(perpToken?.szDecimals || 4),
      type,
    })

    return publishOrder(order)
  }

  const orderSizeUsd = amountParsed.mul(leverage)

  const modifyOrder = async () => {
    if (!currentOrder) throw Error('Position does not exist - use placeOrder')
    if (!meta) throw Error('No Price')

    const modifyOrder = hyperliquidClient.prepareModifyOrder({
      assetId: hyperliquidTokenId,
      price: snapPriceToTick(
        meta.price,
        orderType === 'short' ? 0.9 : 1.1,
        perpToken?.szDecimals || 0
      ),
      size: size.toFixed(perpToken?.szDecimals || 4),
      reduce: editType === 'reduce',
      currentOrder,
    })

    return publishOrder(modifyOrder, true)
  }

  const closePosition = async () => {
    if (!currentOrder) throw Error('Position does not exist - use placeOrder')
    if (!meta) throw Error('No Price')

    const parsedSize = parseFloat(currentOrder.position.szi)

    setLoading(true)

    try {
      const modifyOrder = hyperliquidClient.prepareModifyOrder({
        assetId: hyperliquidTokenId,
        price: snapPriceToTick(meta.price, parsedSize > 0 ? 0.9 : 1.1, perpToken?.szDecimals || 0),
        size: currentOrder.position.szi,
        reduce: true,
        currentOrder,
      })

      await publishOrder(modifyOrder, true)
      refreshPerpsAccountBalance()
    } catch (e) {
      logger.error('Error closing Perp position', { originalError: e })
      setExecutionError((e as Error).toString())
      setLoading(false)

      throw e
    }

    setLoading(false)
  }

  const execute = async () => {
    setLoading(true)

    const method = currentOrder ? modifyOrder : placeOrder

    try {
      await method()
      refreshPerpsAccountBalance()
    } catch (e) {
      logger.error(`PERP Execution error`, { originalError: e })

      setExecutionError((e as Error).toString())
      setLoading(false)

      throw e
    }

    setLoading(false)
  }

  const error = useMemo(() => {
    if (!meta) return t('perps.errors.loadingPrice')
    if (!amountParsed.toNumber()) return t('perps.errors.noAmount')
    if (!perpsAccountBalance || amountParsed.greaterThan(perpsAccountBalance.withdrawableBalance))
      return t('perps.errors.notEnoughBalance')
    if (
      editType === 'reduce' &&
      amountParsed.greaterThan(Decimal(currentOrder?.position.marginUsed || '0'))
    )
      return t('perps.errors.excessiveReduction')

    if (orderSizeUsd.lessThan(MIN_ORDER_SIZE_USD))
      return t('perps.errors.minOrderSize', { size: formatMoney(MIN_ORDER_SIZE_USD) })

    if (editType === 'reduce' && currentOrderSize < size.toNumber())
      return t('perps.errors.notEnoughBalance')
  }, [amountParsed, perpsAccountBalance?.withdrawableBalance, size, orderSizeUsd, currentOrder])

  const orderType = useMemo(() => {
    if (type) return type
    if (!currentOrder) return

    const size = parseFloat(currentOrder.position.szi)

    return size < 0 ? 'short' : 'long'
  }, [type, currentOrder])

  const title = useMemo(() => {
    if (!currentOrder) return t('perps.open', { type: t(`perps.type.${type}`) })

    return `${t(`perps.editType.${editType}`)} ${t(`perps.type.${orderType}`)}`
  }, [currentOrder, type, orderType])

  const liquidationPrice = useMemo(() => {
    if (amountParsed.lessThanOrEqualTo(0)) return
    if (!type || !perpToken || !meta) return

    const floatSide = type === 'long' ? 1 : -1

    const notional = size.mul(meta.markPrice).toNumber()
    const initialMargin = notional / leverage
    const maintananceLeverage = perpToken.maxLeverage * 2
    const correction = 1 - floatSide / maintananceLeverage

    return (
      meta.markPrice -
      (floatSide * (initialMargin - notional / maintananceLeverage)) / size.toNumber() / correction
    )
  }, [amountParsed, meta, type, perpToken])

  const sizeLabel = `${formatNumber(size.toNumber(), token?.symbol)} / ${formatMoney(size.mul(meta?.price || 1).toNumber())}`

  return {
    amount,
    setAmount,
    leverage,
    setLeverage,
    totalAmount: totalAmount.toNumber(),
    size: totalSize.toNumber(),
    execute,
    error,
    balance: perpsAccountBalance?.withdrawableBalance || 0,
    title,
    loading,
    perpToken,
    canChangeLeverage: !currentOrder,
    closePosition,
    executionError,
    sizeLabel,
    liquidationPrice,
    price: meta?.price,
  }
}

export default useCreateOrder
